import { createElement } from '@/utils/dom'
import { router } from '@/utils/router'
import { Footer } from '@/components/footer'

export class FeaturesPage {
  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'features-page' })
    page.innerHTML = this.getPageContent()
    this.attachEventListeners(page)
    
    // Add footer
    const footer = new Footer()
    page.appendChild(footer.render())
    
    return page
  }

  private getPageContent(): string {
    return `
      <!-- Navigation -->
      ${this.createNavigation()}
      
      <div id="funcionalidades-page">
        <div class="container">
          <!-- Hero Section -->
          <section class="hero-section">
            <div class="hero-content">
              <h1>Funcionalidades</h1>
              <p class="hero-description">
              Descubra o simulador inteligente de rotas de negócios que automatiza o registo das viagens mais frequentes dos seus colaboradores.
              </p>
              <div class="hero-stats">
                <div class="stat-item">
                  <span class="stat-number">90%</span>
                  <span class="stat-label">Redução de Tempo</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number">€0,40</span>
                  <span class="stat-label">Por Quilómetro</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number">100%</span>
                  <span class="stat-label">Conformidade</span>
                </div>
              </div>
            </div>
          </section>

          <!-- Core Features -->
          <section class="features-section">
            <h2 class="section-title">Funcionalidades Principais</h2>
            
            <div class="features-grid">
              <!-- Feature 3: Excel Reports -->
              <div class="feature-card">
                <div class="feature-icon">
                  <i class="fas fa-file-excel"></i>
                </div>
                <h3>Relatórios de Viagem Automatizados</h3>
                <p>
                Exportação automática de relatórios Excel com as rotas simuladas mais frequentes, 
                formatados profissionalmente para facilitar o registo interno das deslocações.
                </p>
                <ul class="feature-details">
                  <li>Formato DD/MM/AAAA (padrão português)</li>
                  <li>Cálculos automáticos a €0,40 por quilómetro</li>
                  <li>Dados estruturados para facilitar processos internos</li>
                  <li>Totais e resumos estatísticos incluídos</li>
                </ul>
              </div>

              <!-- Feature 6: Analytics -->
              <div class="feature-card">
                <div class="feature-icon">
                  <i class="fas fa-chart-line"></i>
                </div>
                <h3>Análise de Padrões de Viagem</h3>
                <p>
                Dashboard completo com métricas detalhadas sobre padrões de deslocação, 
                frequência de rotas e análise dos trajetos mais utilizados pelos colaboradores.
                </p>
                <ul class="feature-details">
                <li>Simulações geradas no último mês</li>
                <li>Total de quilómetros e rotas mais frequentes</li>
                <li>Tempo poupado vs. processos manuais</li>
                <li>Tendências de utilização e padrões de viagem mensais</li>
                </ul>
              </div>
            </div>
          </section>

          <!-- Technical Innovation -->
          <section class="innovation-section">
            <h2 class="section-title">Inovações Técnicas</h2>
            
            <div class="innovation-grid">
              <div class="innovation-card">
                <h3>
                  <i class="fas fa-microchip"></i>
                  Arquitetura de 3 Tabelas
                </h3>
                <p>
                  Sistema pioneiro que combina <strong>Utilizadores</strong>, <strong>Cidades</strong> 
                  e <strong>Distâncias</strong> para cache inteligente de dados, reduzindo drasticamente 
                  os custos de API externa.
                </p>
              </div>

              <div class="innovation-card">
                <h3>
                  <i class="fas fa-globe-europe"></i>
                  Integração OpenStreetMap
                </h3>
                <p>
                  Descoberta automática de destinos usando dados abertos do OpenStreetMap, 
                  garantindo cobertura completa do território português sem custos adicionais.
                </p>
              </div>

              <div class="innovation-card">
                <h3>
                  <i class="fas fa-robot"></i>
                  Algoritmo de Balanceamento
                </h3>
                <p>
                Inteligência artificial que ajusta automaticamente distâncias e rotas para 
                simular com precisão os padrões reais de viagem empresarial dos seus colaboradores.
                </p>
              </div>
            </div>
          </section>

          <!-- Process Flow -->
          <section class="process-section">
            <h2 class="section-title">Como Funciona</h2>
            
            <div class="process-flow">
              <div class="process-step">
                <div class="step-number">1</div>
                <div class="step-content">
                  <h3>Configuração Inicial</h3>
                  <p>Insira a morada base da empresa e dados do veículo. O sistema calcula automaticamente todas as distâncias para cidades próximas.</p>
                </div>
              </div>

              <div class="process-arrow">
                <i class="fas fa-arrow-down"></i>
              </div>

              <div class="process-step">
                <div class="step-number">2</div>
                <div class="step-content">
                  <h3>Definição de Parâmetros</h3>
                  <p>Configure o tipo de atividade empresarial, o raio de deslocação (5-100km) e o período para simular as rotas mais frequentes.</p>
                </div>
              </div>

              <div class="process-arrow">
                <i class="fas fa-arrow-down"></i>
              </div>

              <div class="process-step">
                <div class="step-number">3</div>
                <div class="step-content">
                  <h3>Simulação Automática</h3>
                  <p>O algoritmo simula as rotas mais prováveis, distribui viagens pelos dias úteis baseado em padrões empresariais típicos.</p>
                </div>
              </div>

              <div class="process-arrow">
                <i class="fas fa-arrow-down"></i>
              </div>

              <div class="process-step">
                <div class="step-number">4</div>
                <div class="step-content">
                  <h3>Relatório Excel</h3>
                  <p>Download imediato de relatório Excel profissional com as simulações de viagem, pronto para análise interna e ajustes manuais.</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Benefits -->
          <section class="benefits-section">
            <h2 class="section-title">Benefícios para a Sua Empresa</h2>
            
            <div class="benefits-grid">
              <div class="benefit-item">
                <div class="benefit-icon">
                  <i class="fas fa-clock"></i>
                </div>
                <h3>Poupança de Tempo</h3>
                <p>Reduza de 5+ horas mensais para apenas 30 minutos na criação de relatórios de despesas.</p>
              </div>

              <div class="benefit-item">
              <div class="benefit-icon">
              <i class="fas fa-euro-sign"></i>
              </div>
              <h3>Gestão Eficiente</h3>
              <p>Automatize o registo das deslocações empresariais mais frequentes, poupando tempo na gestão administrativa.</p>
              </div>

              <div class="benefit-item">
              <div class="benefit-icon">
              <i class="fas fa-check-circle"></i>
              </div>
              <h3>Organização Profissional</h3>
              <p>Relatórios estruturados e organizados que facilitam a gestão interna das deslocações empresariais.</p>
              </div>
          </section>



          <!-- Use Cases -->
          <section class="use-cases-section">
            <h2 class="section-title">Casos de Uso</h2>
            
            <div class="use-cases-grid">
              <div class="use-case-card">
                <h3>
                  <i class="fas fa-building"></i>
                  Pequenas e Médias Empresas
                </h3>
                <p>
                Empresas com 10-100 funcionários que precisam automatizar o registo e análise 
                das deslocações mais frequentes dos seus colaboradores.
                </p>
                <div class="use-case-example">
                <strong>Exemplo:</strong> Empresa de consultoria que quer identificar as rotas mais comuns 
                dos consultores para otimizar a gestão de deslocações.
                </div>
              </div>

              <div class="use-case-card">
                <h3>
                  <i class="fas fa-calculator"></i>
                  Escritórios de Contabilidade
                </h3>
                <p>
                Gabinetes que servem múltiplos clientes e precisam automatizar a análise 
                de padrões de viagem de forma eficiente e padronizada.
                </p>
                <div class="use-case-example">
                <strong>Exemplo:</strong> Contabilista que gere 20 empresas e precisa identificar 
                padrões de deslocação para cada cliente rapidamente.
                </div>
              </div>

              <div class="use-case-card">
                <h3>
                  <i class="fas fa-chart-pie"></i>
                  Gestores Financeiros
                </h3>
                <p>
                Profissionais responsáveis pela gestão financeira que necessitam analisar 
                padrões de deslocação e otimizar políticas de viagem da empresa.
                </p>
                <div class="use-case-example">
                <strong>Exemplo:</strong> CFO que quer compreender e otimizar os custos de deslocação 
                da empresa através de análise de padrões reais.
                </div>
              </div>
            </div>
          </section>

          <!-- Usage Guidelines -->
          <section class="compliance-section">
          <h2 class="section-title">Uso Responsável</h2>
          
          <div class="compliance-content">
          <div class="compliance-main">
          <h3>Simulador de Padrões de Viagem Empresarial</h3>
          <p>
          O Mapas KM é um simulador que ajuda a identificar as rotas mais frequentes dos colaboradores. 
          As simulações servem como ponto de partida que deve ser validado e ajustado conforme as viagens reais.
          </p>
                
                <div class="compliance-features">
                  <div class="compliance-item">
                  <i class="fas fa-file-contract"></i>
                  <div>
                  <h4>Estrutura Profissional</h4>
                  <p>Relatórios organizados com todos os campos necessários para facilitar o registo interno das deslocações</p>
                  </div>
                  </div>

                  <div class="compliance-item">
                    <i class="fas fa-calendar-alt"></i>
                    <div>
                      <h4>Formatação de Datas</h4>
                      <p>Formato DD/MM/AAAA conforme convenções portuguesas</p>
                    </div>
                  </div>

                  <div class="compliance-item">
                    <i class="fas fa-euro-sign"></i>
                    <div>
                      <h4>Cálculos Precisos</h4>
                      <p>Taxa de €0,40 por quilómetro aplicada corretamente em todos os cálculos</p>
                    </div>
                  </div>

                  <div class="compliance-item">
                  <i class="fas fa-briefcase"></i>
                  <div>
                  <h4>Simulação de Atividades</h4>
                  <p>Sugestões de propósitos comerciais típicos baseadas no tipo de empresa e setor de atividade</p>
                  </div>
                  </div>
                </div>
              </div>

              <div class="compliance-sidebar">
              <div class="compliance-highlight">
              <h4>Uso Recomendado</h4>
              <p>
              Este simulador deve ser usado como <strong>ferramenta de apoio</strong> 
              para identificar padrões de viagem. Todas as simulações devem ser 
              validadas com os dados reais das deslocações efetivamente realizadas.
              </p>
              </div>
              </div>
            </div>
          </section>

         

          <!-- CTA Section -->
          <section class="cta-section">
            <div class="cta-content">
              <h2>Pronto para Automatizar os Seus Relatórios?</h2>
              <p>
              Junte-se a dezenas de empresas portuguesas que já automatizam a análise 
              das suas deslocações empresariais com o nosso simulador.
              </p>
              <div class="cta-buttons">
                <button class="btn btn-primary">
                  <i class="fas fa-user-plus"></i>
                  Começar Agora
                </button>
                <button class="btn btn-secondary">
                  <i class="fas fa-tag"></i>
                  Ver Preços
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    `;
  }

  private createNavigation(): string {
    return `
      <nav class="nav">
        <div class="container">
          <div class="nav-container">
            <div class="nav-left">
              <a href="#" class="nav-logo">
                🗺️ Mapas KM
              </a>
              <ul class="nav-menu">
                <li><a href="/" class="nav-link">Início</a></li>
                <li><a href="/pricing" class="nav-link">Preços</a></li>
                <li><a href="/funcionalidades" class="nav-link active">Funcionalidades</a></li>
              </ul>
            </div>
            <div class="nav-right">
              <a href="/register" class="nav-cta">Começar Grátis →</a>
              <a href="/login" class="nav-login">Login</a>
            </div>
            <button class="mobile-menu-toggle" aria-label="Menu">
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          
          <!-- Mobile Menu -->
          <div class="mobile-menu">
          <div class="mobile-menu-header">
            <div class="mobile-menu-logo">🗺️ Mapas KM</div>
            <button class="mobile-menu-close" aria-label="Fechar menu">✕</button>
          </div>
          <div class="mobile-menu-content">
            <div class="mobile-menu-nav">
              <a href="/" class="mobile-nav-link">Início</a>
              <a href="/pricing" class="mobile-nav-link">Preços</a>
              <a href="/funcionalidades" class="mobile-nav-link active">Funcionalidades</a>
            </div>
            <div class="mobile-menu-actions">
              <a href="/register" class="mobile-nav-cta">Começar Grátis →</a>
              <a href="/login" class="mobile-nav-login">Login</a>
            </div>
          </div>
        </div>
        </div>
      </nav>
    `;
  }

  private attachEventListeners(page: HTMLElement): void {
    // Handle navigation clicks
    const nav = page.querySelector('.nav')
    if (nav) {
      nav.addEventListener('click', (e) => {
        const target = e.target as HTMLElement
        
        // Handle mobile menu toggle
        if (target.closest('.mobile-menu-toggle')) {
          e.preventDefault()
          const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
          const isOpen = mobileMenu.classList.contains('open')
          
          if (isOpen) {
            mobileMenu.classList.remove('open')
            document.body.classList.remove('mobile-menu-open')
          } else {
            mobileMenu.classList.add('open')
            document.body.classList.add('mobile-menu-open')
          }
          return
        }
        
        // Handle mobile menu close button
        if (target.closest('.mobile-menu-close')) {
          e.preventDefault()
          const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
          mobileMenu.classList.remove('open')
          document.body.classList.remove('mobile-menu-open')
          return
        }
        
        // Handle navigation links
        if (target.tagName === 'A') {
          e.preventDefault()
          const href = target.getAttribute('href')
          
          // Close mobile menu if open
          const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
          mobileMenu.classList.remove('open')
          document.body.classList.remove('mobile-menu-open')
          
          if (href === '/' || href === '/pricing' || href === '/register' || href === '/login') {
            router.navigate(href)
          } else if (href === '#' && target.classList.contains('nav-logo')) {
            router.navigate('/')
          }
        }
      })
    }

    // Handle CTA button clicks
    const registerBtn = page.querySelector('.cta-buttons .btn-primary') as HTMLButtonElement
    const pricingBtn = page.querySelector('.cta-buttons .btn-secondary') as HTMLButtonElement

    if (registerBtn) {
      registerBtn.addEventListener('click', (e) => {
        e.preventDefault()
        router.navigate('/register')
      })
    }

    if (pricingBtn) {
      pricingBtn.addEventListener('click', (e) => {
        e.preventDefault()
        router.navigate('/pricing')
      })
    }
  }
}


